import React, { useCallback, useEffect, useState, useRef } from "react";
import { FaUser } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { HiDotsVertical } from "react-icons/hi";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";

import { getReviews, sendReview } from "../service";
import useStore from "../../../store/useStore";
import { errorMessage } from "../../../utils/helper";
import ReadMore from "./ReadMore";
import { pediaContentAttrs, trackZymedEvent, ZYMED_EVENTS } from "../../../utils/zymedAnalytics";

interface Props {
  contentDetails?: any;
}

interface Review {
  id: number;
  userId: number;
  name: string;
  profilePhotoPath: string;
  reviewText: string;
  created: string;
  editable: boolean;
}

const CommentSection: React.FC<Props> = (Props) => {
  const { contentDetails } = Props;

  const navigate = useNavigate();
  const location = useLocation();
  const { tatvaPediaToken } = useStore();
  const getToken = () =>
    tatvaPediaToken || localStorage.getItem("tatvaPediaToken");
  const token = getToken();
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [text, setText] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  const fetchReviews = async () => {
    const res: any = await getReviews(
      contentDetails?.id,
      contentDetails?.contentTypeId,
      token
    );
    if (res !== undefined) {
      setReviews(res?.dtoList);
    } else if (res === undefined) {
      navigate(-1);
    }
  };
  useEffect(() => {
    fetchReviews();
  }, []);

  // Handle mobile keyboard visibility using Visual Viewport API
  useEffect(() => {
    const handleViewportChange = () => {
      // Only apply on mobile devices
      if (window.innerWidth <= 768 && window.visualViewport && window.visualViewport !== null) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = windowHeight - viewportHeight;
        
        // If keyboard height is significant, consider it open
        if (keyboardHeight > 150) {
          setKeyboardVisible(true);
        } else {
          setKeyboardVisible(false);
        }
      }
    };

    // Only add listener on mobile devices
    if (window.innerWidth <= 768 && window.visualViewport && window.visualViewport !== null) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    }
  }, []);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
      // Force re-render on iOS WebView
      if (inputRef.current) {
        inputRef.current.style.opacity = "0.99";
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.style.opacity = "1";
          }
        }, 10);
      }
    },
    []
  );

  const handleFocus = () => {
    setIsFocused(true);
    setKeyboardVisible(true);
    
    // Scroll input into view when keyboard opens (only on mobile)
    if (window.innerWidth <= 768) { // Only on mobile devices
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Force cursor to end of input
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
          
          // Scroll the input into view
          inputRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300); // Increased delay to allow keyboard to fully open
    } else {
      // Desktop behavior - just focus without scrolling
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 100);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    setKeyboardVisible(false);
  };

  const onReviewSubmit = async () => {
    if (text) {
      const res: any = await sendReview(
        contentDetails?.id,
        contentDetails?.contentTypeId,
        text,
        token
      );
      if (res !== undefined && res.status) {
        const { content_id, content_name, therapeutic_area } =
          pediaContentAttrs(contentDetails);
        trackZymedEvent(ZYMED_EVENTS.PEDIA_COMMENT_SUBMITTED, {
          content_id,
          content_name,
          therapeutic_area,
        });
        setText("");
        fetchReviews();
      }
    } else {
      errorMessage("Enter comment", "warning");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between py-3">
        <div className="font-semibold text-[20px] text-[#1F2933]">
          Write your comment
        </div>
        {reviews && reviews.length > 3 && (
          <div
            className="text-sm underline text-primary font-medium"
            onClick={() =>
              navigate("/comment-thread", {
                state: { contentDetails: { ...contentDetails } },
              })
            }
          >
            All Comments
          </div>
        )}
      </div>
      {reviews?.slice(0, 3)?.map((item: any, index: number) => (
        <div key={item?.id} className="flex py-3">
          {item?.profilePhotoPath ? (
            <img
              loading="lazy"
              src={item?.profilePhotoPath}
              className="max-h-8 max-w-8 rounded-3xl"
            />
          ) : (
            <FaUser className="bg-[#F1F1F5] min-h-8 min-w-8 p-2 rounded-3xl text-[#5A6774]" />
          )}
          <div className="ml-2 w-full">
            <div className="flex justify-between">
              <div>
                <div className="text-[#222222] font-bold text-[16px]">
                  {item?.name}
                </div>
                <div className="text-[12px] font-normal text-[#606060]">
                  {moment
                    .utc(item?.created, "YYYY-MM-DD HH:mm:ss")
                    .local()
                    .fromNow()}
                </div>
              </div>
              {/* <HiDotsVertical className="min-w-5 min-h-5" /> */}
            </div>

            <ReadMore>{item?.reviewText}</ReadMore>
          </div>
        </div>
      ))}

      <div className={`py-2 ${keyboardVisible ? 'sticky bottom-0 bg-white z-10 shadow-lg' : ''}`} style={{ 
        paddingBottom: keyboardVisible ? '20px' : '8px',
        marginBottom: keyboardVisible ? '10px' : '0'
      }}>
        <div className="flex overoverflow-hidden border rounded-[10px] border-primary-100 border-[#D0D5DD]">
          <input
            type="text"
            value={text}
            placeholder="Write your comment"
            onChange={handleInputChange}
            ref={inputRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="flex-auto w-full px-3 py-3 outline-none rounded-[10px] transition placeholder:text-[#98A2B3] placeholder:text-[14px] bg-white"
            style={{
              // iOS WebView specific fixes
              WebkitAppearance: "none",
              WebkitUserSelect: "text",
              userSelect: "text",
              transform: "translateZ(0)",
              WebkitTransform: "translateZ(0)",
              willChange: "contents",
              // Force re-render when focused to show cursor
              opacity: isFocused ? 0.99 : 1,
              // Ensure text color is visible
              color: text ? "#475569" : "#94a3b8",
            }}
            // iOS specific attributes
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            className="px-3 uppercase leading-normal text-2xl text-primary"
            onClick={onReviewSubmit}
          >
            <IoMdSend />
          </button>
        </div>
      </div>

      {/* <div className="flex items-center justify-between py-2">
                <div className="relative flex w-full border rounded-[10px] border-primary-100 border-[#D0D5DD]">
                    <input type="text" value={text} className="block flex-auto px-3 py-3 outline-none rounded-[10px] transition placeholder:text-[#98A2B3] placeholder:text-[14px]" placeholder="Write your review" onChange={handleInputChange} />
                    <button className="px-3 uppercase leading-normal text-2xl text-primary" onClick={onReviewSubmit}>
                        <IoMdSend />
                    </button>
                </div>
            </div> */}
    </>
  );
};

export default React.memo(CommentSection);

{
  /* <div className="py-3 flex items-center">
                <div className="bg-[#F1F1F5] text-[#5A6774] w-fit text-[12px] font-medium me-2 px-3 h-[28px] rounded-full flex items-center"><AiOutlineLike className="text-[16px] me-1" /> 40</div>
                <div className="bg-[#F1F1F5] text-[#5A6774] w-fit text-[12px] font-medium me-2 px-3 h-[28px] rounded-full flex items-center"><AiOutlineDislike className="text-[16px] me-1" /> 40</div>
                <div className="bg-[#F1F1F5] text-[#5A6774] w-fit text-[12px] font-medium me-2 px-3 h-[28px] rounded-full flex items-center"><FaRegCommentDots className="text-[16px] me-1" /> Reply</div>
            </div>
                {location.pathname == "/comment-thread" &&
                    Replies.map((reply, index) => (
                        <div key={index} className="flex py-3">
                            <FaUser className="bg-[#F1F1F5] min-h-8 min-w-8 p-2 rounded-3xl text-[#5A6774]" />
                            <div className="ml-2 w-full">
                                <div className="flex justify-between">
                                    <div>
                                        <div className="text-[#222222] font-bold text-[16px]"> {reply.name}</div>
                                        <div className="text-[12px] font-normal text-[#606060]">23 hours ago</div>
                                    </div>
                                    <HiDotsVertical className="min-w-5 min-h-5" />
                                </div>
                                <div className="text-[#454551] text-[14px] font-light mt-2">{reply.text}</div>
                                <div className="py-3 flex items-center">
                                    <div className="bg-[#F1F1F5] text-[#5A6774] w-fit text-[12px] font-medium me-2 px-3 h-[28px] rounded-full flex items-center"><AiOutlineLike className="text-[16px] me-1" /> 40</div>
                                    <div className="bg-[#F1F1F5] text-[#5A6774] w-fit text-[12px] font-medium me-2 px-3 h-[28px] rounded-full flex items-center"><AiOutlineDislike className="text-[16px] me-1" /> 40</div>
                                    <div className="bg-[#F1F1F5] text-[#5A6774] w-fit text-[12px] font-medium me-2 px-3 h-[28px] rounded-full flex items-center"><FaRegCommentDots className="text-[16px] me-1" /> Reply</div>
                                </div>
                                <div className="text-sm underline text-primary font-medium">
                                    Show 2 reply
                                </div>
                            </div>
                        </div>
                    ))
                }
                {location.pathname !== "/comment-thread" &&
                    <div className="text-sm underline text-primary font-medium" onClick={() => navigate('/comment-thread')}>
                        Show 3 reply
                    </div>
                } */
}
